import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom';
import { fetchData } from '../../components/FetchData';
import { LoginUrl } from '../../services/ApiUrls';
import { Eye, EyeOff, Mail, Lock, AlertCircle } from 'lucide-react'
import { Toast } from '../../components/ui/toast'

export default function Login() {
    const navigate = useNavigate()
    const [token, setToken] = useState(false)
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    })
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [loading, setLoading] = useState(false)
    const [loginError, setLoginError] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    
    // Toast states for session expiration notification
    const [showToast, setShowToast] = useState(false)
    const [toastMessage, setToastMessage] = useState('')
    const [toastType, setToastType] = useState<'success' | 'error'>('error')
    
    // Ref to track if we've already handled session expiration (prevents multiple toasts)
    const hasHandledSessionExpired = useRef(false)

    // Check for existing token and redirect
    useEffect(() => {
        if (localStorage.getItem('AccessToken')) {
            navigate('/app/leads/')
        }
    }, [token, navigate])
    
    // Check for session expiration only once on mount
    useEffect(() => {
        // Only check once, even if component re-renders
        if (hasHandledSessionExpired.current) {
            return
        }
        
        // Check if user was redirected due to session expiration
        if (localStorage.getItem('sessionExpired')) {
            hasHandledSessionExpired.current = true
            localStorage.removeItem('sessionExpired') // Clear the flag
            setToastMessage('Your session has expired. Please sign in again.')
            setToastType('error')
            setShowToast(true)
        }
    }, []) // Empty dependency array - only run once on mount

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }))
        }
        setLoginError('')
    }

    const validateForm = () => {
        const newErrors: Record<string, string> = {}
        
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required'
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email'
        }
        
        if (!formData.password.trim()) {
            newErrors.password = 'Password is required'
        }
        
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault()
        
        if (!validateForm()) {
            return
        }
        
        setLoading(true)
        setLoginError('')
        
        const head = {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        }
        
        try {
            const response = await fetchData(`${LoginUrl}/`, 'POST', JSON.stringify(formData), head)
            
            if (response.error) {
                setLoginError(response.message || 'Login failed')
            } else {
                localStorage.setItem('AccessToken', `Bearer ${response.access_token}`)
                localStorage.setItem('RefreshToken', response.refresh_token)
                // Save user ID for later use
                if (response.user_id) {
                    localStorage.setItem('UserId', response.user_id.toString())
                }
                setToken(true)
            }
        } catch (error) {
            console.error('Login error:', error)
            setLoginError('Login failed. Please check your credentials and try again.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
                {/* Login Form Card */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
                    {/* Header inside card */}
                    <div className="text-center mb-8">
                        <div className="mx-auto h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center mb-4">
                            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                            </svg>
                        </div>
                        <h2 className="text-3xl font-bold text-slate-900">Sign in</h2>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-6">
                        {/* Error Alert */}
                        {loginError && (
                            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-md">
                                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                                <p className="text-sm text-red-700">{loginError}</p>
                            </div>
                        )}

                        {/* Email Field */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                                Email Address
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-slate-400" />
                                </div>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`block w-full pl-10 pr-3 py-3 border rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm transition-colors ${
                                        errors.email 
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                                            : 'border-slate-300'
                                    }`}
                                    placeholder="Enter your email"
                                />
                            </div>
                            {errors.email && (
                                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                            )}
                        </div>

                        {/* Password Field */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                                Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-slate-400" />
                                </div>
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    required
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className={`block w-full pl-10 pr-12 py-3 border rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm transition-colors ${
                                        errors.password 
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                                            : 'border-slate-300'
                                    }`}
                                    placeholder="Enter your password"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                                    )}
                                </button>
                            </div>
                            {errors.password && (
                                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-slate-900 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {loading ? (
                                    <div className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Signing in...
                                    </div>
                                ) : (
                                    'Sign in'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            
            {/* Toast Notification */}
            <Toast
                message={toastMessage}
                type={toastType}
                isVisible={showToast}
                onClose={() => setShowToast(false)}
            />
        </div>
    )
}